import { Fragment } from 'react';

import {
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	FormLabel,
	InputLabel,
	MenuItem,
	Select,
	Slider,
	Skeleton,
	TextField,
	Typography,
} from '@mui/material';

import { strToSlug } from '@/utils';
import MultiSelect from '@/components/DynamicForm/FormElements/ui-kits/default/MultiSelect/MultiSelect';
import ImagePickerWithPreview from '@/components/DynamicForm/FormElements/ui-kits/default/ImagePickerWithPreview/ImagePickerWithPreview';
import RadioGroup from '@/components/DynamicForm/FormElements/ui-kits/default/RadioGroup/RadioGroup';
import {
	CollectionInput,
	DateTextInputElement,
	ImagePickerElement,
	MarkdownTextType,
	Message,
	NumberInput,
	RangeInput,
	ReviewCard,
	SimpleInput,
	SkeletonEl,
	UserInteractionEl,
} from '@/components/DynamicForm/types/DynamicFormTypes';
import { cssprefix, flattenIntoKey } from '@/components/DynamicForm/utils/utils';
import MarkdownText from '@/components/DynamicForm/FormElements/ui-kits/default/MarkdownText/MarkdownText';

import { publish } from '../../../events';

import DateTextInput from './DateTextInput/DateTextInput';
import TelInput from './TelInput/TelInput';
import { dynamicFormCheckboxStyles } from './FromElements.styles';

const validSkeletonRadiusObj = {
	sm: '0.25rem',
	md: '0.75rem',
	lg: '1rem',
};

export default {
	select: ({
		name,
		label,
		options = [],
		value,
		placeholder,
		showError,
		errMsg,
		validations,
		reset,
	}: CollectionInput) => (
		<>
			<InputLabel data-testid={`${name}-label`} htmlFor={name}>
				{label}
			</InputLabel>
			<FormControl error={showError}>
				<Select
					name={name}
					id={name}
					value={value as string}
					onChange={(e) => publish('formDataUpdate', [name, e.target.value, validations, reset, 'select'])}
					onBlur={() => publish('markInteracted', name)}
					displayEmpty
					renderValue={(value) => options.find((item) => item.value === value)?.label ?? placeholder}
				>
					{options.map(({ label: optLabel, value: optValue }, index) => (
						<MenuItem key={`${optValue}_${index}`} value={optValue}>
							<Typography variant="bodyMediumBook">{optLabel}</Typography>
						</MenuItem>
					))}
				</Select>
				{errMsg && showError && <FormHelperText>{errMsg}</FormHelperText>}
			</FormControl>
		</>
	),

	checkbox: ({ name, label, value, showError, errMsg, validations, reset }: SimpleInput) => (
		<FormControl data-testid={`${name}-checkbox`} error={showError}>
			<FormGroup>
				<FormControlLabel
					id={name}
					name={name}
					value={value}
					label={<MarkdownText variant="bodyMediumBook" text={label} />}
					onChange={(_, checked) =>
						publish('formDataUpdate', [name, checked, validations, reset, 'checkbox'])
					}
					onBlur={() => publish('markInteracted', name)}
					control={
						<Checkbox
							className={`${cssprefix}checkbox`}
							checked={!!value}
							sx={dynamicFormCheckboxStyles.checkboxStyles}
						/>
					}
					sx={dynamicFormCheckboxStyles.formControlLabelStyles}
				/>
			</FormGroup>
			{errMsg && showError && <FormHelperText>{errMsg}</FormHelperText>}
		</FormControl>
	),

	'multi-select': (props: CollectionInput) => <MultiSelect {...props} />,

	radio: (props: CollectionInput) => <RadioGroup {...props} />,

	//Hold-off
	textarea: ({
		name,
		label,
		placeholder,
		value,
		showError,
		errMsg,
		labelSpacing,
		validations,
		reset,
	}: SimpleInput) => (
		<>
			{(label || labelSpacing) && (
				<InputLabel
					className={`${cssprefix}label-spacer-${labelSpacing && !label ? labelSpacing : 'nn'}`}
					data-testid={`${name}-textarea`}
					htmlFor={name}
				>
					{label || ' '}
				</InputLabel>
			)}
			<TextField
				multiline
				rows={2}
				name={name}
				id={name}
				placeholder={placeholder}
				onChange={(e) => publish('formDataUpdate', [name, e.target.value, validations, reset, e.target.type])}
				onBlur={() => publish('markInteracted', name)}
				defaultValue={(value || '') as string}
				error={showError}
				helperText={<Typography variant="errorValidation">{errMsg}</Typography>}
			/>
		</>
	),

	//Hold-off
	message: ({ title, copy, tagData }: Message) => {
		const titleID = title && title?.length > 0 ? `-${strToSlug(title.join('').slice(0, 15))}-title` : '';
		const copyID = copy && copy?.length > 0 ? `-${strToSlug(copy.join('').slice(0, 15))}-copy` : '';

		return (
			<div
				data-testid={`message${titleID}${copyID}`}
				{...flattenIntoKey(tagData, 'data')}
				className={`${cssprefix}message`}
			>
				{title?.length && (
					<Typography variant="heroMedium" className={`${cssprefix}message-title`} sx={{ mb: '1rem' }}>
						{title.map((text, idx) => (
							<span key={`title_${text.slice(0, 7)}_${idx}`}> {text} </span>
						))}
					</Typography>
				)}
				{copy?.length && (
					<Typography variant="bodyMediumBook" className={`${cssprefix}message-copy`}>
						<MarkdownText text={copy.join('\n\n')} />
					</Typography>
				)}
			</div>
		);
	},

	markdownText: (props: MarkdownTextType) => <MarkdownText {...props} />,

	//Hold-off
	reviewCard: ({ label = '', value, keyList, stepIdx, tagData }: ReviewCard) => (
		<div
			data-testid={`${label}-review-card`}
			{...flattenIntoKey(tagData, 'data')}
			className={`${cssprefix}review-card`}
		>
			<h3>{label}</h3>
			<button
				onClick={(e) => {
					e.preventDefault();
					publish('skipToStep', stepIdx);
				}}
			>
				Edit
			</button>
			<dl>
				{keyList.map((keyName, idx) => (
					<Fragment key={`${keyName}_${idx}`}>
						<dt>{keyName}</dt>
						<dd>{JSON.stringify(value?.[keyName])}</dd>
					</Fragment>
				))}
			</dl>
		</div>
	),

	userInteraction: ({
		label,
		userInteractionNum,
		disabled,
		copy,
		redirect,
		variant = 'primaryDefault',
		size = 'M',
		tagData,
	}: UserInteractionEl) => (
		<Button
			className={`${cssprefix}user-interaction`}
			variant={variant}
			size={size}
			data-testid={`${strToSlug(label)}-user-interaction`}
			{...flattenIntoKey(tagData, 'data')}
			title={label}
			disabled={disabled}
			onClick={(e) => {
				e.preventDefault();
				if (redirect) {
					window.location.href = redirect;
					return;
				}
				if (typeof userInteractionNum === 'number') {
					publish('midStepCall', userInteractionNum);
				}
			}}
		>
			{copy}
		</Button>
	),

	//Hold-off
	range: ({
		name,
		label,
		placeholder,
		value,
		step = 1,
		min = 0,
		max = 10,
		unit = '',
		defaultValue = 0,
		tagData,
		validations,
		reset,
	}: RangeInput) => (
		<>
			<FormLabel htmlFor={name}>
				{label} ({(value || defaultValue) as string} {unit})
			</FormLabel>
			<Slider
				data-testid={`${name}-range`}
				{...flattenIntoKey(tagData, 'data')}
				name={name}
				id={name}
				placeholder={placeholder}
				min={min}
				max={max}
				step={step}
				onChange={(_, value) => publish('formDataUpdate', [name, value, validations, reset, 'range'])}
				onBlur={() => publish('markInteracted', name)}
				value={(value || defaultValue) as number}
			/>
		</>
	),

	number: ({
		name,
		label,
		placeholder,
		value,
		showError,
		errMsg,
		labelSpacing,
		tagData,
		validations,
		reset,
	}: NumberInput) => (
		<>
			{(label || labelSpacing) && (
				<InputLabel
					className={`${cssprefix}label-spacer-${labelSpacing && !label ? labelSpacing : 'nn'}`}
					data-testid={`${name}-number`}
					htmlFor={name}
				>
					{label || ' '}
				</InputLabel>
			)}
			<TextField
				label={placeholder}
				variant="outlined"
				type="number"
				name={name}
				id={name}
				onChange={(e) => publish('formDataUpdate', [name, e.target.value, validations, reset, e.target.type])}
				onBlur={() => publish('markInteracted', name)}
				helperText={<Typography variant="errorValidation">{errMsg}</Typography>}
				error={showError}
				defaultValue={value as number}
				inputProps={flattenIntoKey(tagData, 'data')}
			/>
		</>
	),

	// Need to revisit validation for image picker
	image: ({
		name,
		uploadInfo,
		reviewInfo,
		fileInfo,
		accept,
		centerAlignInfo,
		showError,
		errMsg,
		value,
		validations,
		reset,
	}: ImagePickerElement) => (
		<FormControl>
			<ImagePickerWithPreview
				data-testid={`${name}-image-picker`}
				id={name}
				name={name}
				accept={accept}
				centerAlignInfo={centerAlignInfo}
				fileInfo={fileInfo}
				reviewInfo={reviewInfo}
				uploadInfo={uploadInfo}
				selectedImageFile={value as File}
				showError={showError}
				validations={validations}
				reset={reset}
			/>
			{errMsg && showError && (
				<FormHelperText>
					<Typography variant="errorValidation">{errMsg}</Typography>
				</FormHelperText>
			)}
		</FormControl>
	),

	'date-text-input': (props: DateTextInputElement) => <DateTextInput {...props} />,

	tel: (props: SimpleInput) => <TelInput {...props} />,

	skeleton: ({ animation = 'pulse', height, width, radius, variant = 'text' }: SkeletonEl) => (
		<Skeleton
			animation={animation}
			height={height}
			width={width}
			variant={variant}
			sx={{ borderRadius: validSkeletonRadiusObj[radius as keyof typeof validSkeletonRadiusObj] }}
		/>
	),

	// Handle other HTML5 input types like date, email, etc.
	default: ({
		name,
		label,
		placeholder,
		type,
		value,
		showError,
		errMsg,
		labelSpacing,
		tagData,
		validations,
		reset,
	}: SimpleInput) => (
		<>
			{(label || labelSpacing) && (
				<InputLabel
					className={`${cssprefix}label-spacer-${labelSpacing && !label ? labelSpacing : 'nn'}`}
					data-testid={`${name}-default`}
					htmlFor={name}
				>
					{label || ' '}
				</InputLabel>
			)}
			<TextField
				variant="outlined"
				type={type}
				name={name}
				id={name}
				placeholder={placeholder}
				onChange={(e) => publish('formDataUpdate', [name, e.target.value, validations, reset, e.target.type])}
				onBlur={() => publish('markInteracted', name)}
				defaultValue={value}
				error={showError}
				helperText={<Typography variant="errorValidation">{errMsg}</Typography>}
				inputProps={flattenIntoKey(tagData, 'data')}
			/>
		</>
	),
};
